import { useState, useRef, useEffect, useCallback } from 'react';
import { Pencil, Check } from 'lucide-react';
import { customerImportApi } from '../../api/customers.js';
import { usersApi } from '../../api/users.js';

// ─── CSV helpers ─────────────────────────────────────────────────────────────

function parseCsv(text) {
    const lines = text.replace(/\r/g, '').split('\n').filter(Boolean);
    if (lines.length < 1) return { headers: [], rows: [] };
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const rows = lines.slice(1).map((line, idx) => {
        const cells = splitCsvLine(line);
        const obj = { _id: idx };
        headers.forEach((h, i) => { obj[h] = (cells[i] || '').trim(); });
        return obj;
    });
    return { headers, rows };
}

function splitCsvLine(line) {
    const result = []; let cur = ''; let inQ = false;
    for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') { inQ = !inQ; }
        else if (c === ',' && !inQ) { result.push(cur); cur = ''; }
        else { cur += c; }
    }
    result.push(cur);
    return result;
}

function toCsvText(rows) {
    const headers = COLUMNS.map(c => c.key);
    const header = headers.join(',');
    const lines = rows.map(row =>
        headers.map(h => {
            const v = row[h] || '';
            return v.includes(',') ? `"${v}"` : v;
        }).join(',')
    );
    return [header, ...lines].join('\n');
}

// ─── Column config ────────────────────────────────────────────────────────────

const COLUMNS = [
    { key: 'client_name',   label: 'Client Name',    width: 180, required: true,  type: 'text'   },
    { key: 'type',          label: 'Type',           width: 110, required: true,  type: 'select', options: ['retail', 'corporate'] },
    { key: 'phone',         label: 'Phone',          width: 130, required: false, type: 'text'   },
    { key: 'address',       label: 'Address',        width: 200, required: false, type: 'text'   },
    { key: 'area',          label: 'Area',           width: 130, required: false, type: 'text'   },
    { key: 'customer_type', label: 'Customer Type',  width: 130, required: false, type: 'select', options: ['', 'Assigned', 'Dormant', 'XDifficult', 'Unassigned'] },
    { key: 'score',         label: 'Score',          width: 110, required: false, type: 'select', options: ['', 'HOT', 'MEDIUM', 'COLD', 'ONBOARDED'] },
    { key: 'sales_person',  label: 'Sales Person',   width: 160, required: false, type: 'select-users' },
];

const SCORE_COLORS = { HOT: '#ef4444', MEDIUM: '#f59e0b', COLD: '#3b82f6', ONBOARDED: '#22c55e' };
const TYPE_COLORS  = { retail: '#7c3aed', corporate: '#0369a1' };

// ─── Inline cell component ────────────────────────────────────────────────────

function Cell({ col, value, editing, onChange, userOptions }) {
    const ref = useRef();
    useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

    const display = () => {
        if (!value) return <span style={{ color: '#cbd5e1' }}>—</span>;
        if (col.key === 'type') return (
            <span style={{ fontSize: 11, fontWeight: 600, color: TYPE_COLORS[value] || '#475569',
                background: (TYPE_COLORS[value] || '#475569') + '18', borderRadius: 20, padding: '2px 8px' }}>
                {value}
            </span>
        );
        if (col.key === 'score') return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600,
                color: SCORE_COLORS[value] || '#64748b', background: (SCORE_COLORS[value] || '#64748b') + '18',
                borderRadius: 20, padding: '2px 8px' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: SCORE_COLORS[value] || '#64748b' }} />
                {value}
            </span>
        );
        return <span style={{ fontSize: 13, color: '#334155' }}>{value}</span>;
    };

    if (!editing) {
        return (
            <td style={{ padding: '6px 10px', verticalAlign: 'middle', cursor: 'default',
                borderBottom: '1px solid #f1f5f9', maxWidth: col.width, overflow: 'hidden' }}>
                {display()}
            </td>
        );
    }

    const inputStyle = { width: '100%', border: '1px solid #3b82f6', borderRadius: 4, padding: '4px 7px',
        fontSize: 12, outline: 'none', boxSizing: 'border-box' };

    if (col.type === 'select') {
        return (
            <td style={{ padding: '4px 6px', verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9' }}>
                <select ref={ref} value={value} onChange={e => onChange(e.target.value)}
                    onBlur={() => {}} style={inputStyle}>
                    {col.options.map(o => <option key={o} value={o}>{o || '—'}</option>)}
                </select>
            </td>
        );
    }
    if (col.type === 'select-users') {
        return (
            <td style={{ padding: '4px 6px', verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9' }}>
                <select ref={ref} value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>
                    <option value="">—</option>
                    {userOptions.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                </select>
            </td>
        );
    }
    return (
        <td style={{ padding: '4px 6px', verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9' }}>
            <input ref={ref} type="text" value={value}
                onChange={e => onChange(e.target.value)}
                onKeyDown={e => e.key === 'Escape' && e.target.blur()}
                style={inputStyle} />
        </td>
    );
}

// ─── Drop zone ────────────────────────────────────────────────────────────────

function DropZone({ onFile }) {
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef();

    const accept = (f) => { if (f && f.name.endsWith('.csv')) onFile(f); };
    const handleDrop = (e) => { e.preventDefault(); setDragging(false); accept(e.dataTransfer.files[0]); };

    return (
        <div
            onClick={() => inputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            style={{ border: `2px dashed ${dragging ? '#3b82f6' : '#e2e8f0'}`, borderRadius: 12,
                padding: '40px 24px', textAlign: 'center', cursor: 'pointer',
                background: dragging ? '#eff6ff' : '#f8fafc', transition: 'all 0.15s' }}
        >
            <input ref={inputRef} type="file" accept=".csv" style={{ display: 'none' }}
                onChange={e => accept(e.target.files[0])} />
            <div style={{ color: dragging ? '#3b82f6' : '#cbd5e1', marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#374151' }}>
                {dragging ? 'Drop your CSV here' : 'Drag & drop a CSV file'}
            </div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
                or <span style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 600 }}>browse to upload</span>
            </div>
            <div style={{ marginTop: 14, fontSize: 11, color: '#94a3b8' }}>
                Required columns: <strong>client_name</strong>, <strong>type</strong> (retail / corporate)
            </div>
        </div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CustomerImportPage() {
    const [rows, setRows]           = useState([]);
    const [selected, setSelected]   = useState(new Set());
    const [editingRowId, setEditingRowId] = useState(null);
    const [users, setUsers]         = useState([]);
    const [loading, setLoading]     = useState(false);
    const [result, setResult]       = useState(null);
    const [fileName, setFileName]   = useState('');
    const tableRef = useRef();

    useEffect(() => {
        usersApi.getAll({ is_active: true, limit: 200 })
            .then(r => setUsers((r.data || r || []).map(u => ({ id: u.id, name: u.name?.trim().toUpperCase() })).filter(u => u.name)))
            .catch(() => {});
    }, []);

    const handleFile = useCallback(async (file) => {
        setResult(null);
        setFileName(file.name);
        const text = await file.text();
        const { rows: parsed } = parseCsv(text);
        // Ensure all COLUMNS keys exist
        const normalised = parsed.map((r, i) => {
            const row = { _id: i };
            COLUMNS.forEach(c => { row[c.key] = r[c.key] || ''; });
            return row;
        });
        setRows(normalised);
        setSelected(new Set(normalised.map(r => r._id)));
        setEditingRowId(null);
    }, []);

    const toggleAll = () => {
        setSelected(selected.size === rows.length ? new Set() : new Set(rows.map(r => r._id)));
    };

    const toggleRow = (id) => {
        const next = new Set(selected);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelected(next);
    };

    const handleChange = (rowId, colKey, value) => {
        setRows(prev => prev.map(r => r._id === rowId ? { ...r, [colKey]: value } : r));
    };

    const isRowValid = (row) => row.client_name?.trim() && ['retail', 'corporate'].includes(row.type?.trim().toLowerCase());

    const handleUpload = async () => {
        const toUpload = rows.filter(r => selected.has(r._id));
        if (!toUpload.length) return;
        setLoading(true); setResult(null);
        try {
            const csvText = toCsvText(toUpload);
            const res = await customerImportApi.importCsv(csvText);
            setResult(res);
        } catch (err) {
            setResult({ error: err?.response?.data?.error || 'Upload failed.' });
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => { setRows([]); setSelected(new Set()); setResult(null); setFileName(''); };

    const handleDownloadSample = async () => {
        const res = await customerImportApi.downloadSample();
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url; a.download = 'customers_import_sample.csv'; a.click();
        URL.revokeObjectURL(url);
    };

    const validCount   = rows.filter(r => selected.has(r._id) && isRowValid(r)).length;
    const invalidCount = rows.filter(r => selected.has(r._id) && !isRowValid(r)).length;

    // Click outside table → stop editing
    useEffect(() => {
        const handler = (e) => { if (tableRef.current && !tableRef.current.contains(e.target)) setEditingRowId(null); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div className="page-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <style>{`
                .cip-table th { position: sticky; top: 0; background: #f8fafc; z-index: 2; font-size: 11px;
                    font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em;
                    padding: 10px; border-bottom: 1px solid #e2e8f0; white-space: nowrap; }
                .cip-table tr:hover td { background: #f8fafc; }
                .cip-row-invalid td { background: #fff5f5 !important; }
                .cip-row-invalid:hover td { background: #fee2e2 !important; }
            `}</style>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Customer Import</div>
                    <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Upload a CSV, preview &amp; edit, then import selected rows</div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button onClick={handleDownloadSample} style={{ display: 'flex', alignItems: 'center', gap: 7,
                        background: 'none', border: '1px solid #e2e8f0', borderRadius: 8,
                        padding: '8px 16px', fontSize: 13, cursor: 'pointer', color: '#2563eb', fontWeight: 600 }}>
                        <DownloadIcon /> Sample CSV
                    </button>
                    {rows.length > 0 && (
                        <>
                            <button onClick={handleReset} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8,
                                padding: '8px 16px', fontSize: 13, cursor: 'pointer', color: '#64748b' }}>
                                ↑ New File
                            </button>
                            <button onClick={handleUpload} disabled={loading || validCount === 0}
                                style={{ display: 'flex', alignItems: 'center', gap: 7, background: loading || validCount === 0 ? '#94a3b8' : '#16a34a',
                                    color: '#fff', border: 'none', borderRadius: 8, padding: '8px 22px', fontSize: 13,
                                    fontWeight: 600, cursor: loading || validCount === 0 ? 'not-allowed' : 'pointer' }}>
                                {loading
                                    ? <><Spinner /> Uploading…</>
                                    : <><UploadIcon /> Upload {validCount} Row{validCount !== 1 ? 's' : ''}</>
                                }
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Drop zone (shown when no data) */}
            {rows.length === 0 && !result && <DropZone onFile={handleFile} />}

            {/* Result banner */}
            {result && (
                <div style={{ borderRadius: 10, padding: '16px 20px',
                    background: result.error ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${result.error ? '#fecaca' : '#bbf7d0'}` }}>
                    {result.error ? (
                        <div style={{ color: '#dc2626', fontWeight: 600, fontSize: 13 }}>{result.error}</div>
                    ) : (
                        <>
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#15803d', marginBottom: 4 }}>
                                ✓ {result.inserted} customer{result.inserted !== 1 ? 's' : ''} imported
                                {result.skipped > 0 && (
                                    <span style={{ marginLeft: 10, background: '#fef3c7', color: '#92400e', borderRadius: 4,
                                        padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{result.skipped} skipped</span>
                                )}
                            </div>
                            {result.skippedRows?.length > 0 && (
                                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    {result.skippedRows.map((r, i) => (
                                        <div key={i} style={{ fontSize: 12, color: '#78350f', background: '#fef3c7',
                                            borderRadius: 4, padding: '3px 10px' }}>
                                            Row {r.row}: <strong>{r.name}</strong> — {r.reason}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Preview table */}
            {rows.length > 0 && (
                <div className="card" style={{ overflow: 'hidden' }}>
                    {/* Table toolbar */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 16px', borderBottom: '1px solid #f1f5f9', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                                {fileName}
                            </span>
                            <span style={{ fontSize: 12, color: '#64748b' }}>{rows.length} rows</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            {validCount > 0 && (
                                <span style={{ fontSize: 11, fontWeight: 600, color: '#15803d',
                                    background: '#dcfce7', borderRadius: 20, padding: '3px 10px' }}>
                                    {validCount} valid
                                </span>
                            )}
                            {invalidCount > 0 && (
                                <span style={{ fontSize: 11, fontWeight: 600, color: '#dc2626',
                                    background: '#fee2e2', borderRadius: 20, padding: '3px 10px' }}>
                                    {invalidCount} invalid (missing required fields)
                                </span>
                            )}
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>Use the Actions column to edit a row</span>
                        </div>
                    </div>

                    {/* Scrollable table */}
                    <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }} ref={tableRef}>
                        <table className="cip-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: 44 }} />
                                {COLUMNS.map(c => <col key={c.key} style={{ width: c.width }} />)}
                                <col style={{ width: 80 }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'center', padding: 10 }}>
                                        <input type="checkbox" checked={selected.size === rows.length && rows.length > 0}
                                            onChange={toggleAll}
                                            style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#2563eb' }} />
                                    </th>
                                    {COLUMNS.map(c => (
                                        <th key={c.key} style={{ textAlign: 'left' }}>
                                            {c.label}
                                            {c.required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
                                        </th>
                                    ))}
                                    <th style={{ textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(row => {
                                    const isSelected = selected.has(row._id);
                                    const isValid    = isRowValid(row);
                                    const isEditingRow = editingRowId === row._id;
                                    return (
                                        <tr key={row._id}
                                            className={isSelected && !isValid ? 'cip-row-invalid' : ''}
                                            style={{ opacity: isSelected ? 1 : 0.45 }}>
                                            {/* Checkbox */}
                                            <td style={{ textAlign: 'center', padding: '6px 10px',
                                                borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' }}>
                                                <input type="checkbox" checked={isSelected}
                                                    onChange={() => toggleRow(row._id)}
                                                    style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#2563eb' }} />
                                            </td>
                                            {/* Data cells */}
                                            {COLUMNS.map(col => (
                                                <Cell
                                                    key={col.key}
                                                    col={col}
                                                    value={row[col.key] || ''}
                                                    editing={isEditingRow}
                                                    onChange={v => handleChange(row._id, col.key, v)}
                                                    userOptions={users}
                                                />
                                            ))}
                                            {/* Actions */}
                                            <td style={{ textAlign: 'center', padding: '6px 10px',
                                                borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' }}>
                                                <button
                                                    onClick={() => setEditingRowId(isEditingRow ? null : row._id)}
                                                    title={isEditingRow ? 'Done' : 'Edit'}
                                                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                        width: 26, height: 26, padding: 0, borderRadius: 6, cursor: 'pointer',
                                                        border: `1px solid ${isEditingRow ? '#bbf7d0' : '#dbeafe'}`,
                                                        background: isEditingRow ? '#f0fdf4' : '#eff6ff',
                                                        color: isEditingRow ? '#16a34a' : '#2563eb' }}
                                                >
                                                    {isEditingRow ? <Check size={13} /> : <Pencil size={12} />}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function DownloadIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
    );
}

function UploadIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
    );
}

function Spinner() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" style={{ animation: 'spin 0.8s linear infinite' }}>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
    );
}
